import { Activity, CircleDollarSign, Clock3, Database, HelpCircle, ShieldAlert, X } from 'lucide-react';
import type { Channel, RecentDecision, ScoreBreakdown } from '../api';
import { PRIVACY_MASK, maskChannelName } from '../privacy';

interface DetailDrawerProps {
  channel: Channel | null;
  recentDecisions: RecentDecision[];
  privacyMode?: boolean;
  onClose: () => void;
}

export function DetailDrawer({ channel, recentDecisions, privacyMode = false, onClose }: DetailDrawerProps) {
  if (!channel) return null;

  const errorDetails = Array.isArray(channel.errorDetails)
    ? channel.errorDetails.filter((error) => error.count30m > 0)
    : [];
  const requestTrend = Array.isArray(channel.requestTrend) ? channel.requestTrend : [];
  const probeLogs = Array.isArray(channel.probeLogs) ? channel.probeLogs : [];
  const scoreParts = Array.isArray(channel.scoreParts) ? channel.scoreParts : [];
  const healthWindows = Array.isArray(channel.healthWindows) ? channel.healthWindows : [];
  const safeRecentDecisions = Array.isArray(recentDecisions) ? recentDecisions : [];
  const accountDecisions = safeRecentDecisions
    .filter((decision) => decision.selectedAccountId === channel.id || (Array.isArray(decision.scores) && decision.scores.some((score) => score.AccountID === channel.id)))
    .slice(0, 3);
  const latestDecision = accountDecisions[0];
  const latestDecisionScores = orderDecisionScores(latestDecision);
  const latestScore = latestDecisionScores.find((score) => score.AccountID === channel.id);
  const trendTotal = requestTrend.reduce((sum, point) => sum + point.success + point.failed, 0);
  const trendSuccess = requestTrend.reduce((sum, point) => sum + point.success, 0);
  const trendFailed = requestTrend.reduce((sum, point) => sum + point.failed, 0);
  const trendSuccessRate = trendTotal > 0 ? (trendSuccess / trendTotal) * 100 : null;
  const dispatchRows = channel.dispatchScore ? [
    { label: '实时稳定性', value: channel.dispatchScore.components.stability, max: 1000, tone: 'good' },
    { label: '延迟', value: channel.dispatchScore.components.latency, max: 120, tone: 'neutral' },
    { label: '缓存亲和', value: channel.dispatchScore.components.cacheAffinity, max: 220, tone: 'good' },
    { label: 'Sub2API 原生优先级惩罚', value: channel.dispatchScore.components.nativePriority, max: 200, tone: 'warn' },
    { label: '手动优先级', value: channel.dispatchScore.components.manualPriority, max: 200, tone: 'neutral' },
    { label: '成本', value: channel.dispatchScore.components.costPenalty, max: 180, tone: 'warn' },
    { label: '切换惩罚', value: channel.dispatchScore.components.switchingPenalty, max: 60, tone: 'warn' },
    { label: '并发占用', value: channel.dispatchScore.components.inflightPenalty, max: 200, tone: 'warn' },
    { label: '健康状态', value: channel.dispatchScore.components.healthPenalty, max: 150, tone: 'bad' },
  ] : [];
  const displayName = privacyMode ? maskChannelName(channel.name) : channel.name;

  return (
    <div className="drawer-backdrop" role="presentation" onClick={onClose}>
      <aside
        className="drawer"
        role="dialog"
        aria-modal="true"
        aria-label={displayName}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="drawer-header">
          <div>
            <span className="eyebrow">{channel.providerLabel} · {channel.group.name}</span>
            <h2>{displayName}</h2>
          </div>
          <button className="icon-button" type="button" aria-label="关闭详情" onClick={onClose}>
            <X size={18} />
          </button>
        </header>

        <section className="drawer-section">
          <div className="section-title">
            <h3><Activity size={16} /> 真实调度评分</h3>
            <Help title="当前 /select 会使用的总分。这里按无会话上下文估算，所以缓存亲和与切换惩罚通常为 0；真实请求带 session 时会动态变化。" />
          </div>
          {channel.dispatchScore ? (
            <>
              <div className="dispatch-total">
                <span>当前 /select 会使用的总分</span>
                <strong>{formatNumber(channel.dispatchScore.total)}</strong>
              </div>
              <div className="score-list dispatch-score-list">
                {dispatchRows.map((part) => (
                  <ScoreRow key={part.label} label={part.label} value={part.value} max={part.max} tone={part.tone} />
                ))}
              </div>
              <div className="health-window-grid" aria-label="多窗口健康指标">
                {healthWindows.map((window) => (
                  <div className="health-window" key={window.window}>
                    <strong>{window.window}</strong>
                    <span>调度样本 {window.requests.toLocaleString()}</span>
                    <span className={(window.failures ?? 0) > 0 ? 'tone-text-bad' : 'tone-text-good'}>
                      失败 {(window.failures ?? 0).toLocaleString()}
                    </span>
                    <span>成功率 {formatPercent(window.successRate)}</span>
                    <span>慢请求率 {formatPercent(window.slowRate)}</span>
                    <span className={window.severeSlowCount > 0 ? 'tone-text-bad' : 'tone-text-good'}>
                      严重慢请求 {window.severeSlowCount.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <span className="muted">暂无调度评分</span>
          )}
        </section>

        <section className="drawer-section">
          <div className="section-title">
            <h3><Activity size={16} /> 最近调度决策</h3>
            <Help title="进程内最近 /select 决策，不写数据库。用于解释刚才为什么选中某个账号，服务重启后会清空。" />
          </div>
          {latestDecision ? (
            <div className="decision-box">
              <div className="decision-head">
                <span>{latestDecision.model || channel.model} · {formatDecisionReason(latestDecision)}</span>
                <strong>{latestDecision.selectedAccountId === channel.id ? '已选中' : '候选'}</strong>
              </div>
              <div className="decision-meta">
                <span>{formatDecisionTime(latestDecision.createdAt)}</span>
                <span>{latestDecision.requestId}</span>
              </div>
              <div className="decision-scores" aria-label="候选账号分数对比">
                {latestDecisionScores.slice(0, 4).map((score) => (
                  <div className={score.AccountID === channel.id ? 'decision-score selected' : 'decision-score'} key={score.AccountID}>
                    <span className="decision-account-name" title={privacyMode ? PRIVACY_MASK : decisionAccountLabel(score)}>
                      {privacyMode ? maskDecisionAccountLabel(score) : decisionAccountLabel(score)}
                    </span>
                    <span className="decision-account-cost">
                      {privacyMode ? PRIVACY_MASK : formatDecisionCost(score.costCnyPerUsd)}
                    </span>
                    <strong>{formatNumber(score.Total)}</strong>
                  </div>
                ))}
              </div>
              {latestScore && (
                <p className="decision-reasons">
                  {decisionPenaltyReasons(latestScore).join(' / ') || '无明显惩罚'}
                </p>
              )}
            </div>
          ) : (
            <span className="muted">暂无最近调度决策</span>
          )}
        </section>

        <section className="drawer-section">
          <div className="section-title">
            <h3><ShieldAlert size={16} /> 后台健康评分</h3>
            <Help title="用于页面概览的健康拆分，不等同于真实调度总分。真实调度以上方 /select 评分为准。" />
          </div>
          <div className="score-list">
            {scoreParts.map((part) => (
              <div className="score-row" key={part.label}>
                <span>{part.label}</span>
                <div className="score-meter" aria-hidden="true">
                  <span className={`score-fill tone-${part.tone}`} style={{ width: `${Math.min(Math.abs(part.value) / part.max, 1) * 100}%` }} />
                </div>
                <strong>{part.value > 0 ? `+${part.value}` : part.value}</strong>
              </div>
            ))}
          </div>
        </section>

        <section className="drawer-section">
          <div className="section-title">
            <h3><ShieldAlert size={16} /> 错误记录</h3>
            <Help title="近24小时真实请求与探测失败的最近错误记录；次数为近30分钟内相同状态码错误数量。" />
          </div>
          <div className="error-list" aria-label="近24小时错误记录">
            {errorDetails.length === 0 ? <span className="muted">暂无错误</span> : (
              <>
                <div className="error-row error-row-head">
                  <span>状态码</span>
                  <span>错误</span>
                  <span>错误信息</span>
                  <span>近30分钟</span>
                </div>
                {errorDetails.map((error) => (
                  <div className="error-row" key={`${error.statusCode}-${error.errorType}-${error.lastSeenAt}`}>
                    <strong>{error.statusCode > 0 ? error.statusCode : '-'}</strong>
                    <span>{errorTypeLabel(error.errorType)}</span>
                    <span className="error-message" title={error.errorMessage}>{error.errorMessage || '-'}</span>
                    <span>{error.count30m.toLocaleString()} 次</span>
                  </div>
                ))}
              </>
            )}
          </div>
        </section>

        <section className="drawer-section">
          <div className="section-title">
            <h3><Clock3 size={16} /> 探测日志</h3>
            <Help title="SubPilot 主动探测产生的最近记录；未开启探测时这里可能为空，真实请求仍会参与调度评分。" />
          </div>
          <div className="probe-log-list">
            {probeLogs.length === 0 ? <span className="muted">暂无探测日志</span> : probeLogs.map((log) => (
              <div className="probe-log-row" key={`${log.time}-${log.message}`}>
                <span className={`dot status-${log.status}`} />
                <div>
                  <strong>{probeStatusLabel(log.status)}</strong>
                  <span>{formatProbeTime(log.time)}</span>
                </div>
                <span className="probe-log-latency">{formatProbeLatency(log.latencyMs)}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="drawer-section">
          <div className="section-title">
            <h3><Database size={16} /> 真实请求趋势</h3>
            <Help title="按近24小时真实请求汇总，包含 Sub2API 对账号实际发起的成功和失败请求；这不是 Sub2API 渠道探测的 7 天可用率。" />
          </div>
          <div className="request-summary" aria-label="真实请求趋势">
            <Metric label="24h 总请求" value={privacyMode ? PRIVACY_MASK : trendTotal.toLocaleString()} />
            <Metric label="成功" value={privacyMode ? PRIVACY_MASK : trendSuccess.toLocaleString()} tone="good" />
            <Metric label="失败" value={privacyMode ? PRIVACY_MASK : trendFailed.toLocaleString()} tone={trendFailed > 0 ? 'bad' : 'neutral'} />
            <Metric label="成功率" value={trendSuccessRate == null ? '暂无数据' : `${trendSuccessRate.toFixed(2)}%`} />
          </div>
        </section>

        <section className="drawer-kpis">
          <div>
            <span><Database size={15} /> 缓存 tokens <Help title="缓存 token 反映该账号近24小时缓存命中规模。数值高时，调度器应尽量避免频繁切换账号，以保留上下文缓存收益。" /></span>
            <strong>{formatInteger(channel.cacheTokens24h)}</strong>
          </div>
          <div>
            <span><CircleDollarSign size={15} /> 24h 综合成本</span>
            <strong>{privacyMode ? PRIVACY_MASK : `${formatCost(channel.compositeCost24h)} 元/美元`}</strong>
          </div>
        </section>
      </aside>
    </div>
  );
}

function ScoreRow({ label, value, max, tone }: { label: string; value: number; max: number; tone: string }) {
  return (
    <div className="score-row">
      <span>{label}</span>
      <div className="score-meter" aria-hidden="true">
        <span className={`score-fill tone-${tone}`} style={{ width: `${Math.min(Math.abs(value) / max, 1) * 100}%` }} />
      </div>
      <strong>{formatSigned(value)}</strong>
    </div>
  );
}

function Metric({ label, value, tone = 'neutral' }: { label: string; value: string; tone?: 'good' | 'bad' | 'neutral' }) {
  return (
    <div className={`request-metric tone-text-${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function Help({ title }: { title: string }) {
  return (
    <span className="help-icon" tabIndex={0} aria-label={title} data-tooltip={title}>
      <HelpCircle size={15} />
    </span>
  );
}

function formatSigned(value: number): string {
  const rounded = formatNumber(value);
  return value > 0 ? `+${rounded}` : rounded;
}

function formatNumber(value: number): string {
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}

function formatInteger(value: number | undefined): string {
  return Number.isFinite(value) ? Number(value).toLocaleString() : '0';
}

function formatCost(value: number | undefined): string {
  return Number.isFinite(value) ? Number(value).toFixed(4) : '0.0000';
}

function formatDecisionCost(value: number | undefined): string {
  return Number.isFinite(value) ? `${Number(value).toFixed(4)} 元/美元` : '未设置';
}

function formatPercent(value: number): string {
  return `${value.toFixed(2)}%`;
}

function formatDecisionReason(decision: RecentDecision): string {
  if (decision.reasonText?.trim()) {
    return decision.reasonText.trim();
  }
  const labels: Record<string, string> = {
    highest_score: '最高总分',
    sticky: '缓存亲和',
    balanced: '综合均衡',
    cost_first: '成本优先',
    cost_tie_break: '成本打平',
    fallback_degraded: '降级兜底',
    fallback_unhealthy: '异常兜底',
    forced_account: '强制账号',
    forced_account_unavailable: '强制账号不可用',
    latency_fallback: '延迟兜底',
    no_dispatchable_channel: '无可调度账号',
  };
  return labels[decision.reason] ?? decision.reason;
}

function formatDecisionTime(value: string): string {
  const timestamp = Date.parse(value);
  if (Number.isNaN(timestamp)) return value;
  return new Date(timestamp).toLocaleString();
}

function errorTypeLabel(value: string): string {
  const labels: Record<string, string> = {
    auth_error: '鉴权错误',
    quota_exhausted: '额度耗尽',
    rate_limited: '限速',
    overloaded: '上游过载',
    timeout: '超时',
    network_error: '网络错误',
    server_error: '上游/服务端错误',
    unknown_error: '其他错误',
    model_error: '模型错误',
  };
  return labels[value] ?? value;
}

function probeStatusLabel(status: Channel['status']): string {
  const labels: Record<Channel['status'], string> = {
    healthy: '探测成功',
    degraded: '探测降级',
    cooldown: '探测冷却',
    failed: '探测失败',
  };
  return labels[status] ?? status;
}

function formatProbeLatency(value: number | null): string {
  return value == null ? '未采集' : `耗时 ${value.toLocaleString()}ms`;
}

function formatProbeTime(value: string): string {
  const parsed = Date.parse(value);
  if (Number.isNaN(parsed)) return value;
  return new Date(parsed).toLocaleString('zh-CN', { hour12: false });
}

function decisionPenaltyReasons(score: ScoreBreakdown): string[] {
  const reasons: string[] = [];
  if (score.Components.Stability < 700) reasons.push('最近窗口稳定性或慢请求惩罚');
  if (score.Components.HealthPenalty < 0) reasons.push('健康状态惩罚');
  if (score.Components.CostPenalty < 0) reasons.push('成本扣分');
  if (score.Components.InflightPenalty < 0) reasons.push('并发占用惩罚');
  if (score.Components.SwitchingPenalty < 0) reasons.push('切换惩罚');
  return reasons;
}

function decisionAccountLabel(score: ScoreBreakdown): string {
  return score.accountName?.trim() || score.AccountID;
}

function maskDecisionAccountLabel(score: ScoreBreakdown): string {
  return score.accountName ? maskChannelName(score.accountName) : PRIVACY_MASK;
}

function orderDecisionScores(decision: RecentDecision | undefined): ScoreBreakdown[] {
  const scores = Array.isArray(decision?.scores) ? [...decision.scores] : [];
  if (decision?.reason === 'cost_first') {
    return scores.sort((left, right) => {
      const leftCost = normalizedDecisionCost(left.costCnyPerUsd);
      const rightCost = normalizedDecisionCost(right.costCnyPerUsd);
      if (leftCost !== rightCost) return leftCost - rightCost;
      return right.Total - left.Total;
    });
  }
  return scores;
}

function normalizedDecisionCost(value: number | undefined): number {
  return Number.isFinite(value) && Number(value) > 0 ? Number(value) : Number.POSITIVE_INFINITY;
}
